/**
 * The module's core and recommended resources.
 *
 * @module components/studio/panels/ReadingPanel
 */

import React from "react";

import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import {
  Button,
  EmptyState,
  IconButton,
  Panel,
  useConfirm,
  useEditLock,
  useFocusOnAdd,
} from "@/components/ui";
import { FormField, SelectField, TextField } from "@/components/ui/Form";
import type { ReadingListItem } from "@/types";

const RESOURCE_TYPES = [
  { value: "core", label: "Core / essential" },
  { value: "recommended", label: "Recommended" },
];

export function ReadingPanel({
  readingList,
  editor,
}: {
  readingList: readonly ReadingListItem[];
  editor: ModuleEditorState;
}): React.ReactElement {
  const confirm = useConfirm();
  const focusOnAdd = useFocusOnAdd();
  // The lock leaves the Add button off, so the empty state gives its reason instead.
  const lock = useEditLock();

  const remove = async (item: ReadingListItem, index: number): Promise<void> => {
    const proceed = await confirm({
      title: `Remove resource ${index + 1}?`,
      body: "This removes the reading-list resource from the module.",
      confirmLabel: "Remove resource",
      tone: "danger",
      "data-testid": "module-remove-reading-confirm",
    });
    if (proceed) {
      editor.removeReading(item.id);
    }
  };

  return (
    <Panel
      aria-label="Reading list"
      description="List the core and recommended resources for this module."
      actions={
        <Button
          variant="outline"
          icon="plus"
          onClick={() => {
            focusOnAdd(`module-reading-type-${editor.addReading()}`);
          }}
          data-testid="module-workspace-add-reading"
        >
          Add reading item
        </Button>
      }
    >
      <div className="divide-y divide-base-300">
        {readingList.length ? (
          readingList.map((item, index) => (
            <div
              key={item.id}
              className="py-5"
              data-testid={`module-workspace-reading-${index + 1}`}
            >
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[repeat(12,minmax(0,1fr))_auto] xl:items-end">
                <FormField
                  label="Resource type"
                  htmlFor={`module-reading-type-${item.id}`}
                  helpText="Mark whether this reading is core/essential or recommended."
                  className="xl:col-span-3"
                >
                  <SelectField
                    id={`module-reading-type-${item.id}`}
                    value={item.type ?? (item.isCore ? "core" : "recommended")}
                    onChange={(value) =>
                      editor.updateReading(item.id, (draft) => {
                        draft.type = value;
                        draft.isCore = value === "core";
                      })
                    }
                    options={RESOURCE_TYPES}
                  />
                </FormField>
                <FormField
                  label="Author"
                  htmlFor={`module-reading-author-${item.id}`}
                  helpText="List primary author names as they should appear in module documentation."
                  className="xl:col-span-3"
                >
                  <TextField
                    id={`module-reading-author-${item.id}`}
                    value={item.author ?? ""}
                    onChange={(value) =>
                      editor.updateReading(item.id, (draft) => {
                        draft.author = value;
                      })
                    }
                  />
                </FormField>
                <FormField
                  label="Title"
                  htmlFor={`module-reading-title-${item.id}`}
                  helpText="Enter the full resource title used by learners and reviewers."
                  className="xl:col-span-4"
                >
                  <TextField
                    id={`module-reading-title-${item.id}`}
                    value={item.title ?? item.citation ?? ""}
                    onChange={(value) =>
                      editor.updateReading(item.id, (draft) => {
                        draft.title = value;
                        // The export renderers prefer a legacy `citation`
                        // over `title`; leaving it set would hide this edit.
                        draft.citation = undefined;
                      })
                    }
                  />
                </FormField>
                <FormField
                  label="Year"
                  htmlFor={`module-reading-year-${item.id}`}
                  helpText="Enter the publication year so out-of-date or replaced resources are easy to spot."
                  className="xl:col-span-2"
                >
                  <TextField
                    id={`module-reading-year-${item.id}`}
                    value={item.year ?? ""}
                    onChange={(value) =>
                      editor.updateReading(item.id, (draft) => {
                        draft.year = value;
                      })
                    }
                  />
                </FormField>
                <IconButton
                  icon="trash"
                  variant="ghost"
                  aria-label={`Remove resource ${index + 1}`}
                  onClick={() => remove(item, index)}
                />
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <FormField
                  label="Publisher"
                  htmlFor={`module-reading-publisher-${item.id}`}
                  helpText="Enter the publisher or issuing source."
                >
                  <TextField
                    id={`module-reading-publisher-${item.id}`}
                    value={item.publisher ?? ""}
                    onChange={(value) =>
                      editor.updateReading(item.id, (draft) => {
                        draft.publisher = value;
                      })
                    }
                  />
                </FormField>
                <FormField
                  label="ISBN"
                  htmlFor={`module-reading-isbn-${item.id}`}
                  helpText="Optional. The ISBN helps people check and find the resource."
                >
                  <TextField
                    id={`module-reading-isbn-${item.id}`}
                    value={item.isbn ?? ""}
                    onChange={(value) =>
                      editor.updateReading(item.id, (draft) => {
                        draft.isbn = value;
                      })
                    }
                  />
                </FormField>
              </div>
            </div>
          ))
        ) : (
          <EmptyState
            icon="books"
            title="No reading resources yet"
            description={
              lock.locked
                ? lock.reason
                : "Add the core and recommended resources learners will use."
            }
          />
        )}
      </div>
    </Panel>
  );
}

export default ReadingPanel;
